/**
 * @file
 * A wrapper for Contentful API:
 * https://contentful.github.io/contentful.js/
 */

import isPlainObject from 'lodash/isPlainObject';
import isObject from 'lodash/isObject';

import { APP_LOCALES } from 'locale/locale.config';

import { reporter } from 'lib/reporter';

function lengthInWords(length, isVideo) {
  if (!length) {
    return null;
  }

  const lengthSplitted = length.split(':');
  const minutes = Number.parseInt(lengthSplitted[0]);
  const seconds = lengthSplitted[1];
  const verb = isVideo ? 'watch' : 'read';

  return minutes === 0 ? `${seconds} sec ${verb}` : `${minutes} min ${verb}`;
}

/**
 * Map CMS content types to their Contentful type ID
 */
const contentTypes = {
  INFO_CENTER_ARTICLE: 'infoCenterArticle',
  INFO_CENTER_CATEGORY: 'infoCenterCategory',
  INFO_CENTER_SUBCATEGORY: 'infoCenterSubcategory',
  INFO_CENTER_ARTICLE_PROMOS: 'articlePromos',
  GENERIC_CONTENT: 'genericContent',
  PAGE_METADATA: 'metadata',
  LOCALE: 'locale',
};

/**
 * Map CMS content types to their Contentful entry ID
 */
const entityIds = {
  INFO_CENTER_HERO_PROMOS: {
    id: '7aFjlqJcb4wCq5KcPcw4CS',
    query: { include: 3 },
  },
  INFO_CENTER_NISSA_PROMOS: {
    id: 'vRB7cxEFcgE8r1BjUndjq',
    query: { include: 3 },
  },
  INFO_CENTER_QA_PROMOS: {
    id: '5PdvI424MiyFXMjNx9RIdD',
    query: { include: 3 },
  },
  INFO_CENTER_TOP_BAR_MENU: {
    id: '3pvAa6dCGpE3oNQCPqxjtJ',
    query: { include: 2 },
  },
  INFO_CENTER_TILE_MENU: {
    id: 'Grc3GcUN96wohJvY3z26n',
    query: { include: 2 },
  },
  PAGE_HOMEPAGE: {
    [APP_LOCALES.UK]: '3A0ZwsQx2uFuwvor4V7YWG',
    [APP_LOCALES.IE]: '4M24NtyruAy0d7e1R2qDev',
    dipUti: '5DJv1azspYYWLSvYnrNoqS',
    query: { include: 2 },
  },
  PAGE_FAQ: {
    [APP_LOCALES.UK]: '66k8pyBZwGCABoXJG67LbQ',
    [APP_LOCALES.IE]: '4hS1lEZAW86ZYxJfKVvaod',
    dipUti: '7IlDC22Puay4DLIQ8s3eX6',
    mobileApp: '1HDlic9auWiuz397HbnrWH',
    query: { include: 2 },
  },
  PAGE_PRIVACY_POLICY: {
    [APP_LOCALES.UK]: '7lXZp5Ov6GIXE3NkIcE0zN',
    [APP_LOCALES.IE]: '7lXZp5Ov6GIXE3NkIcE0zN',
    dipUti: '5Wh3bKDCL4Xz1vMqIsnUs2',
  },
  PAGE_TERMS_AND_CONDITIONS: {
    [APP_LOCALES.UK]: '5sZKLQrj8gajRxmJyBpuNF',
    [APP_LOCALES.IE]: '5sZKLQrj8gajRxmJyBpuNF',
    dipUti: '7haY9HxYbhMDGwnhnRpz8i',
  },
  PAGE_ELIGIBILITY: {
    [APP_LOCALES.UK]: 'BLYm11qbkn0cAJrGkojcE',
    [APP_LOCALES.IE]: 'BLYm11qbkn0cAJrGkojcE',
    dipUti: '43mHNHqC5ITVg8HuEntx27',
  },
  PAGE_COOKIE_NOTICE: {
    [APP_LOCALES.UK]: '3qV6i5qw987CqFXksTViTq',
    [APP_LOCALES.IE]: '3qV6i5qw987CqFXksTViTq',
    dipUti: '3qV6i5qw987CqFXksTViTq',
  },
  PAGE_DELIVERY_OPTIONS: {
    [APP_LOCALES.UK]: '1TUG0K80RcYeU8YEFQZvVd',
  },
};

let client;

/**
 * Lazy load the Contentful client to minimize bundle size and page load time.
 */
async function getClient() {
  if (client) {
    return client;
  }

  const { createClient } = await import(
    /* webpackChunkName: "contentful" */ 'contentful'
  );

  client = createClient({
    space: process.env.CONTENTFUL_SPACE_ID,
    accessToken: process.env.CONTENTFUL_ACCESS_TOKEN,
  });

  return client;
}

function isContentfulRefType(value) {
  return value && isPlainObject(value) && value.fields;
}

/**
 * Recursive function that will flatten the contentful raw response data and
 * resolve all nested entities for easier consumption.
 *
 * @param {object} rawDataObj
 */
function serializer(rawDataObj) {
  // Set fields to be rawDataObj in case that rawDataObj is an object but not
  // comply to any logic down below.
  let data = rawDataObj;

  // Check of the data is an object or an array, if not return the data
  // This is a stop condition for our recursion.
  if (!isObject(rawDataObj)) {
    return rawDataObj;
  }

  // Check if the type of the object is a contentful reference type,
  // if so extract the 'fields' and 'sys' properties.
  if (isContentfulRefType(rawDataObj)) {
    data = rawDataObj.fields;
    data.sys = rawDataObj.sys;
  }

  /**
   * Check if rawDataObj is an array, if so map over the array and call
   * serializer() for each element.
   *
   * Entities from contentful that have list of other entities
   * will be presented as an array.
   *
   * @example
   * faqCategories: [{sys:{...}, fields:{...}},{...},...]
   */
  if (Array.isArray(rawDataObj)) {
    data = rawDataObj.map(rawData => serializer(rawData));

    return data;
  }

  const result = Object.entries(data).reduce((acc, [key, value]) => {
    acc[key] = serializer(value);

    return acc;
  }, {});

  return result;
}

/**
 * Fetch a collection of entries by query
 * @see
 * https://www.contentful.com/developers/docs/references/content-delivery-api/#/reference/entries
 *
 * @param {Object} query
 */
async function fetchEntries(query) {
  const client = await getClient();
  const data = await client.getEntries(query);

  return serializer(data);
}

/**
 * Fetch an entry by Entry ID
 *
 * @see
 * https://contentful.github.io/contentful.js/contentful/7.14.4/ContentfulClientAPI.html#.getEntry
 *
 * @param {string} id
 * @param {Object} query
 */

async function fetchEntryById(id, query) {
  const client = await getClient();
  const data = await client.getEntry(id, query);

  return serializer(data);
}

/**
 * Utility function to shape category
 *
 * @param {Object} category Raw category
 */
function shapeCategory(category) {
  return {
    createdAt: category?.sys?.createdAt ?? null,
    image: shapeImage(category?.image),
    isVideo: Boolean(category?.videoId),
    icon: shapeImage(category?.icon),
    metaTitle: category?.metaTitle ?? null,
    metaDescription: category?.metaDescription ?? null,
    length: category?.length ?? null,
    lengthDescribed: lengthInWords(
      category?.length,
      Boolean(category?.videoId)
    ),
    linkAs: category?.slug ? `/info-center/${category.slug}` : null,
    linkHref: category ? `/info-center/[category]` : null,
    reviewer: category?.reviewer ?? null,
    slug: category?.slug ?? null,
    title: category?.title ?? null,
    updatedAt: category?.sys?.updatedAt ?? null,
    videoId: category?.videoId ?? null,
  };
}

/**
 * Utility function to shape an image
 *
 * @param {Object} image
 */
function shapeImage(image) {
  return {
    contentType: image?.file?.contentType ?? null,
    description: image?.description ?? null,
    height: image?.file?.details?.image?.height ?? null,
    title: image?.title ?? null,
    width: image?.file?.details?.image?.width ?? null,
    url: image?.file?.url ?? null,
  };
}

/**
 * Utility function to shape subcategory
 *
 * @param {Object} subcategory Raw subcategory
 */
function shapeSubcategory(subcategory) {
  return {
    category: shapeCategory(subcategory?.category),
    createdAt: subcategory?.sys?.createdAt ?? null,
    image: shapeImage(subcategory?.image),
    isVideo: Boolean(subcategory?.videoId),
    metaTitle: subcategory?.metaTitle ?? null,
    metaDescription: subcategory?.metaDescription ?? null,
    length: subcategory?.length ?? null,
    lengthDescribed: lengthInWords(
      subcategory?.length,
      Boolean(subcategory?.videoId)
    ),
    linkAs:
      subcategory?.category?.slug && subcategory?.slug
        ? `/info-center/${subcategory.category.slug}/${subcategory.slug}`
        : null,
    linkHref: subcategory ? '/info-center/[category]/[subcategory]' : null,
    reviewer: subcategory?.reviewer ?? null,
    slug: subcategory?.slug ?? null,
    title: subcategory?.title ?? null,
    updatedAt: subcategory?.sys?.updatedAt ?? null,
    videoId: subcategory?.videoId ?? null,
  };
}

/**
 * Utility function to shape article
 *
 * @param {Object} article Raw article
 */
function shapeArticle(article) {
  return {
    category: shapeCategory(article?.subcategory?.category),
    createdAt: article?.sys?.createdAt ?? null,
    image: shapeImage(article?.image),
    isVideo: Boolean(article?.videoId),
    metaTitle: article?.metaTitle ?? null,
    metaDescription: article?.metaDescription ?? null,
    length: article?.length ?? null,
    lengthDescribed: lengthInWords(article?.length, Boolean(article?.videoId)),
    linkAs:
      article?.subcategory?.category?.slug && article?.subcategory?.slug
        ? `/info-center/${article.subcategory.category.slug}/${article.subcategory.slug}/${article.slug}`
        : null,
    linkHref: article
      ? '/info-center/[category]/[subcategory]/[article]'
      : null,
    promoTitle: article.promoTitle ?? null,
    reviewer: article?.reviewer ?? null,
    richText: article?.text ?? null,
    slug: article?.slug ?? null,
    subcategory: shapeSubcategory(article?.subcategory),
    thumbnail: shapeImage(article?.thumbnail),
    title: article?.title ?? null,
    updatedAt: article?.sys.updatedAt ?? null,
    videoId: article?.videoId ?? null,
  };
}

/**
 * Utility function to shape an array of articles
 *
 * @param {Array} articles
 */
function shapeArticles(articles) {
  return articles?.length > 0
    ? articles.map(article => shapeArticle(article))
    : [];
}

/**
 * Utility function to shape categories
 *
 * @param {Object} entries Raw category entries
 */
function shapeCategories(entries) {
  const items = entries?.items ?? [];

  return items.map(item => shapeCategory(item));
}

/**
 * Utility function to shape subcategories
 *
 * @param {Object} subcategories Raw subcategory entries
 */
function shapeSubcategories(entries) {
  const items = entries?.items ?? [];

  return items.map(item => shapeSubcategory(item));
}

/**
 * Utility function to shape promos
 *
 * @param {Object} promos Raw promos
 */
function shapePromos(promos) {
  let ctaLinkAs;
  let ctaLinkHref;

  if (promos.linkedCta) {
    const isSubcategory = Boolean(promos.linkedCta.category);

    promos.linkedCta = isSubcategory
      ? shapeSubcategory(promos.linkedCta)
      : shapeCategory(promos.linkedCta);

    ctaLinkAs = isSubcategory
      ? `/info-center/${promos.linkedCta.category.slug}/${promos.linkedCta.slug}`
      : `/info-center/${promos.linkedCta.slug}`;

    ctaLinkHref = isSubcategory
      ? `/info-center/[category]/[subcategory]`
      : `/info-center/[category]`;
  }

  const promosTitle = promos?.title ?? promos?.linkedCta?.title ?? null;

  return {
    articles: shapeArticles(promos?.articles),
    ctaLinkAs: ctaLinkAs ?? null,
    ctaLinkHref: ctaLinkHref ?? null,
    ctaText: promos?.ctaText ?? null,
    linkedCta: promos?.linkedCta ?? null,
    removeCta: promos?.removeCta ?? null,
    renderAsList: promos?.renderAsList ?? null,
    showOnLobby: promos?.showOnLobby ?? null,
    title: promos.removeTitle ? null : promosTitle,
  };
}

/**
 * Utility function to shape entries
 *
 * @param {Object} entries Raw entries
 */
function shapeEntries(entries) {
  return {
    items: entries?.items ?? null,
    limit: entries?.limit ?? null,
    skip: entries?.skip ?? null,
    total: entries?.total ?? null,
  };
}

/**
 * Utility function to shape menu item
 *
 * @param {Object} menuItem Raw menu item
 */
function shapeMenuItem(menuItem) {
  return {
    icon: shapeImage(menuItem?.icon),
    label: menuItem?.label ?? null,
    href: menuItem?.href ?? null,
    slug: menuItem?.slug ?? null,
  };
}

/**
 * Utility function to shape subcategories
 *
 * @param {Object} subcategories Raw subcategory entries
 */
function shapeMenu(menu) {
  const menuItems = menu?.menuItems ?? [];

  return menuItems.map(menuItem => shapeMenuItem(menuItem));
}

/**
 * Fetch and shape categories
 */
async function fetchCategories() {
  const rawCategories = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_CATEGORY,
    order: 'fields.order',
  });

  return shapeCategories(rawCategories);
}

/**
 * Fetch an article by a category from Contentful.
 *
 * @param {string} articleSlug
 * @param {string} subcategorySlug
 * @param {string} categorySlug
 */
async function fetchArticle(articleSlug, subcategorySlug, categorySlug) {
  // Check that the sub-category has the requested category as a parent
  const subcategoryEntries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_SUBCATEGORY,
    'fields.slug': subcategorySlug,
    'fields.category.sys.contentType.sys.id': contentTypes.INFO_CENTER_CATEGORY,
    'fields.category.fields.slug': categorySlug,
  });

  // If the /[category]/[subcategory] combination doesn't add up, return nothing
  if (subcategoryEntries.total === 0) {
    return null;
  }

  // Check that the article exists and has the requested sub-category
  const articleEntries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_ARTICLE,
    'fields.slug': articleSlug,
    'fields.subcategory.sys.contentType.sys.id':
      contentTypes.INFO_CENTER_SUBCATEGORY,
    'fields.subcategory.fields.slug': subcategorySlug,
    include: 3,
  });

  return articleEntries.total > 0
    ? shapeArticle(articleEntries.items[0])
    : null;
}

/**
 * Get all articles from Contentful.
 *
 * @param {number} limit The maximum number of results
 */
async function fetchAllArticles(limit = 100) {
  const articleEntries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_ARTICLE,
    include: 2,
    limit,
  });

  return shapeArticles(articleEntries.items);
}

/**
 * Fetch articles which are highlighted as promos, by a subcategory.
 *
 * @param {string} subcategorySlug
 * @param {string} articleSlug An article slug not to include within the promos
 */
async function fetchArticlePromosBySubcategory(subcategorySlug, articleSlug) {
  const articles = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_ARTICLE,
    /*
    Use Contentful's filtering to exclude the article which is
    being viewed already. `ne` stands for "not equal".
    */
    'fields.slug[ne]': articleSlug,
    'fields.subcategory.sys.contentType.sys.id':
      contentTypes.INFO_CENTER_SUBCATEGORY,
    'fields.subcategory.fields.slug': subcategorySlug,
    'fields.showAsPromo': true,
    limit: 6,
    include: 2,
  });

  return shapeArticles(articles.items);
}

/**
 * Fetch promos for lobby.
 */
async function fetchLobbyCategoryPromos() {
  const rawCategoriesPromosForLobby = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_ARTICLE_PROMOS,
    limit: 10,
    include: 2,
    'fields.showOnLobby': true,
  });

  return rawCategoriesPromosForLobby.total > 0
    ? rawCategoriesPromosForLobby.items.map(promos => shapePromos(promos))
    : [];
}

/**
 * Fetch promos for Info Center lobby's hero section.
 */
async function fetchInfoCenterHeroPromos() {
  const { id, query } = entityIds.INFO_CENTER_HERO_PROMOS;
  const promos = await fetchEntryById(id, query);

  return shapeArticles(promos.articles);
}

/**
 * Fetch promos for Info Center lobby's Nissa section.
 */
async function fetchLobbyNissaPromos() {
  const { id, query } = entityIds.INFO_CENTER_NISSA_PROMOS;
  const promos = await fetchEntryById(id, query);
  const shapedPromos = shapePromos(promos);

  // Hard-code the call-to-action's URL (as the content won't have it)
  shapedPromos.ctaLinkHref = '/info-center/dr-nissa';

  return shapedPromos;
}

/**
 * Fetch promos for Info Center lobby's Q&As section.
 */
async function fetchLobbyQAPromos() {
  const { id, query } = entityIds.INFO_CENTER_QA_PROMOS;
  const promos = await fetchEntryById(id, query);
  const shapedPromos = shapePromos(promos);

  // Hard-code the call-to-action's URL (as the content won't have it)
  shapedPromos.ctaLinkHref = '/info-center/uti-questions';

  return shapedPromos;
}

/**
 * Fetch menu items for Info Center top bar menu.
 */
async function fetchTopBarMenu() {
  const { id, query } = entityIds.INFO_CENTER_TOP_BAR_MENU;
  const menu = await fetchEntryById(id, query);

  return shapeMenu(menu);
}

/**
 * Fetch menu items for Info Center lobby's tile menu.
 */
async function fetchTileMenu() {
  const { id, query } = entityIds.INFO_CENTER_TILE_MENU;
  const menu = await fetchEntryById(id, query);

  return shapeMenu(menu);
}

/**
 * Fetch articles which are marked as articles which relate to
 * Dr. Nissa's page.
 *
 * @param {number} limit Maximum number of results
 * @param {number} skip Results offset
 */
async function fetchDrNissaArticles(limit = 40, skip = 0) {
  const entries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_ARTICLE,
    'fields.showOnNissa': true,
    include: 2,
    limit,
    skip,
  });

  const shapedEntries = shapeEntries(entries);
  shapedEntries.items = shapeArticles(shapedEntries.items);

  return shapedEntries;
}

/**
 * Fetch articles which are marked as articles which relate to
 * Questions and Answers page.
 *
 * @param {number} limit Maximum number of results
 * @param {number} skip Results offset
 */
async function fetchQuestionArticles(limit = 40, skip = 0) {
  const entries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_ARTICLE,
    'fields.isQA': true,
    include: 2,
    limit,
    skip,
  });

  const shapedEntries = shapeEntries(entries);
  shapedEntries.items = shapeArticles(shapedEntries.items);

  return shapedEntries;
}

/**
 * Fetch a category by a slug.
 *
 * @param {string} categorySlug
 */
async function fetchCategory(categorySlug) {
  const entries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_CATEGORY,
    limit: 1,
    'fields.slug': categorySlug,
  });

  return entries.total > 0 ? shapeCategory(entries.items[0]) : null;
}

/**
 * Fetch a category's rich text content by a category slug.
 *
 * @param {string} categorySlug
 */
async function fetchCategoryRichText(categorySlug) {
  const entries = await fetchEntries({
    content_type: contentTypes.GENERIC_CONTENT,
    'fields.id': categorySlug,
    include: 3,
    limit: 1,
  });

  return entries.items[0]?.richText ?? null;
}

/**
 * Fetch articles from Contentful which are highlighted as promos,
 * and aren't a part of a given subcategory.
 *
 * @param {string} subcategorySlug Subcategory slug
 */
async function fetchSubcategoryPagePromos(subcategorySlug) {
  const entries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_ARTICLE,
    'fields.subcategory.sys.contentType.sys.id':
      contentTypes.INFO_CENTER_SUBCATEGORY,
    // Exclude articles by a subcategory
    'fields.subcategory.fields.slug[ne]': subcategorySlug,
    'fields.showAsPromo': true,
    limit: 6,
    include: 3,
  });

  return shapeArticles(entries.items);
}

/**
 * Fetch all subcategories from Contentful.
 */
async function fetchAllSubcategories() {
  const entries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_SUBCATEGORY,
  });

  return shapeSubcategories(entries);
}

/**
 * Fetch a subcategory from Contenful.
 *
 * @param {string} subcategorySlug
 * @param {string} categorySlug
 */
async function fetchSubcategory(subcategorySlug, categorySlug) {
  const entries = await fetchEntries({
    content_type: contentTypes.INFO_CENTER_SUBCATEGORY,
    'fields.slug': subcategorySlug,
    'fields.category.sys.contentType.sys.id': contentTypes.INFO_CENTER_CATEGORY,
    'fields.category.fields.slug': categorySlug,
  });

  return entries.total > 0 ? shapeSubcategory(entries.items[0]) : null;
}

/**
 * Fetch a subcategory's rich text content by a subcategory slug.
 *
 * @param {string} subcategorySlug
 */
async function fetchSubcategoryRichText(subcategorySlug) {
  try {
    const entries = await fetchEntries({
      content_type: contentTypes.GENERIC_CONTENT,
      'fields.id': subcategorySlug,
      include: 3,
      limit: 1,
    });

    return entries.items[0]?.richText ?? null;
  } catch (error) {
    console.error(error);

    reporter.error(error, {
      error,
      file: 'contentfulApi.js',
      method: 'fetchSubcategoryRichText',
      subcategorySlug,
    });

    return null;
  }
}

/**
 * Fetch a single content entry
 *
 * @param {string} entryName
 * @param {string} localeCode
 */
async function fetchEntry(entryName, filterBy) {
  try {
    const { [filterBy]: entryId, query } = entityIds?.[entryName];

    if (!entryId) {
      throw new Error(`Failed to get entry ID`);
    }

    const client = await getClient();
    const entry = await client.getEntry(entryId, query);

    return serializer(entry);
  } catch (error) {
    reporter.error(new Error('Failed to fetch content from Contentful'), {
      error,
      file: 'contentfulApi.js',
      method: 'fetchEntry',
      entryName,
      filterBy,
    });
  }
}

function shapePageMetadata(entry) {
  return {
    description: entry?.description ?? null,
    localeCode: entry?.locale?.localeCode ?? null,
    ogDescription: entry?.ogDescription ?? null,
    ogImageUrl: entry?.ogImageUrl ?? null,
    ogTitle: entry?.ogTitle ?? null,
    ogVideoUrl: entry?.ogVideoUrl ?? null,
    pageSlug: entry?.pageSlug ?? null,
    title: entry?.title ?? null,
    url: entry?.url ?? null,
  };
}

async function fetchPageMetadata({ pageSlug, localeCode, isDipUti }) {
  try {
    const entries = await fetchEntries({
      content_type: contentTypes.PAGE_METADATA,
      'fields.pageSlug': pageSlug,
      'fields.locale.sys.contentType.sys.id': contentTypes.LOCALE,
      'fields.locale.fields.localeCode': localeCode,
      'fields.isDipUti': isDipUti,
    });

    return shapePageMetadata(entries.items[0]);
  } catch (error) {
    reporter.error(new Error('Failed to fetch page metadata'), {
      error,
      file: 'contentfulApi.js',
      method: 'fetchPageMetadata',
      pageSlug,
      localeCode,
      isDipUti,
    });
  }

  return {};
}

function shapeLimitedNotice(entry) {
  return entry
    ? {
        price: entry.text1 ?? null,
        date: entry.text2 ?? null,
      }
    : null;
}

async function fetchLimitedNotice(localeCode) {
  try {
    const entries = await fetchEntries({
      content_type: contentTypes.GENERIC_CONTENT,
      'fields.id': 'limitedNotice',
      'fields.locale.sys.contentType.sys.id': contentTypes.LOCALE,
      'fields.locale.fields.localeCode': localeCode,
      limit: 1,
    });

    return shapeLimitedNotice(entries.items[0]);
  } catch (error) {
    console.error(error);

    reporter.error(new Error('Failed to fetch limitedNotice'), {
      error,
      file: 'contentfulApi.js',
      method: 'fetchLimitedNotice',
      localeCode,
    });

    return null;
  }
}

export const contentfulApi = {
  contentTypes,
  shapeArticle,
  shapePromos,
  fetchCategories,
  fetchAllSubcategories,
  fetchArticle,
  fetchAllArticles,
  fetchArticlePromosBySubcategory,
  fetchLobbyCategoryPromos,
  fetchInfoCenterHeroPromos,
  fetchLobbyNissaPromos,
  fetchLobbyQAPromos,
  fetchDrNissaArticles,
  fetchCategory,
  fetchCategoryRichText,
  fetchQuestionArticles,
  fetchSubcategoryPagePromos,
  fetchSubcategory,
  fetchSubcategoryRichText,
  fetchEntry,
  fetchPageMetadata,
  fetchLimitedNotice,
  fetchTopBarMenu,
  fetchTileMenu,
};
